import React, { useState } from "react";
import nodata from "../../assets/images/no-data.png";

/**
 * PrescriptionDataListingV3
 * Version 3 (7.0) only — supports:
 *  - Confidence-based color highlighting (green = 100, red < 100)
 *  - Editable fields
 *  - Uses "Corrected Medicine Name" instead of "Medicine Name"
 */

// Keys that are confidence scores — used only for coloring, never rendered
const isConfidenceKey = (key) => key.toLowerCase().endsWith(" confidence");

// Given a section object, find the confidence value for a field key
const getConfidence = (sectionObj, fieldKey) => {
  const confKey = `${fieldKey} Confidence`;
  const val = sectionObj[confKey];
  return val !== undefined ? Number(val) : null;
};

// Confidence → CSS border-left color class
const confidenceClass = (confidence) => {
  if (confidence === null) return "";
  return confidence >= 100 ? "rx_conf_green" : "rx_conf_red";
};

// EditableField — renders a single key/value row with inline editing + confidence highlight
function EditableField({ label, value, confidence }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedValue, setEditedValue] = useState(
    value !== null && value !== undefined && String(value) !== "None"
      ? String(value)
      : "",
  );

  const displayValue = editedValue !== "" ? editedValue : "N/A";

  const confClass = confidenceClass(confidence);

  return (
    <div
      className={`col-12 py-2 document_values pb-0 pb-sm-2 rx_field_row ${confClass}`}
    >
      <div className="row align-items-center">
        <div className="col-12 font_500 col-md-6 text_gray8 text-capitalize">
          {label}
        </div>
        <div className="col-12 col-md-6 pt-1 pt-sm-0 mb-3 mb-sm-0">
          {isEditing ? (
            <div className="d-flex align-items-center gap-2">
              <input
                type="text"
                className="form-control form-control-sm rx_edit_input"
                value={editedValue}
                autoFocus
                onChange={(e) => setEditedValue(e.target.value)}
                onBlur={() => setIsEditing(false)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === "Escape")
                    setIsEditing(false);
                }}
              />
            </div>
          ) : (
            <div className="d-flex justify-content-start align-items-center gap-2">
              <span className="text_master2 fw_600 word_break text-start">
                {displayValue}
              </span>
              <span
                className="rx_edit_icon cursor-pointer"
                title="Edit"
                onClick={() => setIsEditing(true)}
              >
                <i className="fas fa-pen font_11 text_gray8"></i>
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Renders a flat section object (Patient Details / Doctor Details / top-level Prescription fields)
// Skips confidence keys; uses getConfidence for highlighting
function SectionFields({ sectionObj }) {
  return Object.entries(sectionObj).map(([key, value]) => {
    // Skip confidence keys — only used for coloring
    if (isConfidenceKey(key)) return null;
    // Skip nested objects/arrays (handled separately)
    if (typeof value === "object" && value !== null) return null;

    const confidence = getConfidence(sectionObj, key);
    const displayLabel = key;

    return (
      <EditableField
        key={key}
        label={displayLabel}
        value={value}
        confidence={confidence}
      />
    );
  });
}

// Renders a single medicine card from the Medicines array
function MedicineCard({ medicine, index }) {
  // Build display fields, replacing "Medicine Name" with "Corrected Medicine Name"
  const displayFields = [];

  Object.entries(medicine).forEach(([key, value]) => {
    if (isConfidenceKey(key)) return;
    // Skip the raw "Medicine Name" — use "Corrected Medicine Name" instead
    if (key === "Medicine Name") return;

    const label = key === "Corrected Medicine Name" ? "Medicine Name" : key;

    const confidence = getConfidence(medicine, key);

    displayFields.push(
      <EditableField
        key={key}
        label={label}
        value={value}
        confidence={confidence}
      />,
    );
  });

  return (
    <div className="rx_medicine_card mb-3 px-3 py-2 rounded">
      <h6 className="font_13 fw-bold text_master2 mb-2">
        Medicine {index + 1}
      </h6>
      <div className="row">{displayFields}</div>
    </div>
  );
}

function PrescriptionDataListingV3({ responseData }) {
  const result = responseData?.detail?.result || responseData?.result || {};

  const hasData = result && Object.keys(result).length > 0;

  return (
    <div className="extracted_data_section pb-3 overflow-auto scrollbar1 px-0 px-md-3 px-md-0 position-relative">
      <div className="pt-3 pt-md-0">
        <h2 className="font_16 fw-bold mb-3 text-center pt-2 mb-2">
          Extracted Data
        </h2>
      </div>

      {hasData ? (
        <div className="px-3 pt-3 pb-1 font_13">
          {Object.entries(result).map(([sectionKey, sectionValue]) => {
            if (typeof sectionValue !== "object" || sectionValue === null)
              return null;

            return (
              <div key={sectionKey} className="col-12 mb-3">
                <h5 className="font_15 fw-bold text_master2 mt-2 mb-2">
                  {sectionKey}
                </h5>

                {sectionKey === "Prescription Details" ? (
                  <div className="ps-2">
                    {/* Flat fields in Prescription Details (excluding Medicines array) */}
                    <div className="row">
                      <SectionFields sectionObj={sectionValue} />
                    </div>

                    {/* Medicines array */}
                    {Array.isArray(sectionValue["Medicines"]) &&
                      sectionValue["Medicines"].length > 0 && (
                        <div className="mt-2">
                          <h6 className="font_13 fw_500 text_gray8 mb-2">
                            Medicines
                          </h6>
                          {sectionValue["Medicines"].map((med, idx) => (
                            <MedicineCard
                              key={idx}
                              medicine={med}
                              index={idx}
                            />
                          ))}
                        </div>
                      )}
                  </div>
                ) : (
                  <div className="row ps-2">
                    <SectionFields sectionObj={sectionValue} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="d-flex flex-column align-items-center mt-0 mt-md-5">
          <img
            src={nodata}
            className="no_data_thumbnail object_fit_contain"
            alt="No Data"
          />
          <h4 className="text_gray7 font_16 mt-3">Unable To Process Data</h4>
        </div>
      )}
    </div>
  );
}

export default PrescriptionDataListingV3;
